import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  styled,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Button,
  Typography
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PageHeader from '../../../components/PageHeader';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatDate } from '../../../util';
import { api } from '../../../api';
import { Company } from '../../../schema/company';

const BlacklistCompanyEdit: React.FC = () => {
  const { company_id } = useParams();
  const navigate = useNavigate();

  const [blacklist_companyType, setBlacklistType] = useState<'permanent' | 'temporary'>(
    'permanent'
  );

  const [company, setCompany] = useState<Company | null>(null);

  const [reason, setReason] = useState<string>('');
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDelete = () => {
    if (isLoading) {
      return;
    }
    if (company_id) {
      setIsLoading(true);
      api
        .delete(`blacklist_company/${company_id}`)
        .then(() => {
          navigate('/blacklist');
        })
        .finally(() => setIsLoading(false));
    }
  };
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) {
      return;
    }
    if (company_id) {
      setIsLoading(true);
      api
        .put(`blacklist_company/${company_id}`, {
          company_id: company?.id,
          blacklist_end_date:
            blacklist_companyType == 'permanent' ? null : endDate ? formatDate(endDate) : null,
          reason: reason
        })
        .then(() => {
          navigate('/blacklist');
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (company_id) {
      api.get<Company>(`/company/${company_id}`).then((res) => {
        const { data } = res;
        setCompany(data);
      });
      api.get(`/blacklist_company/${company_id}`).then((res) => {
        const { data } = res;
        const { blacklist_company_end_date, reason } = data;
        if (blacklist_company_end_date) {
          setBlacklistType('temporary');
          setEndDate(new Date(blacklist_company_end_date));
        }
        if (reason) {
          setReason(reason);
        }
      });
    }
  }, []);

  return (
    <Container>
      <PageHeader text="แก้ไขข้อมูล Blacklist" />
      <Button component={Link} to={`/blacklist_company`} sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        blacklist_company
      </Button>

      {/* <Typography variant="body2" sx={{ paddingY: 3 }}>
        เพิ่มข้อมูลการขอแลกบัตรล่วงเวลา
      </Typography> */}
      <Paper>
        <Typography variant="h6" sx={{ p: 2 }}>
          แก้ไขข้อมูล
        </Typography>
        <Form onSubmit={handleFormSubmit} sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="เลขที่ผู้เสียภาษี"
                variant="outlined"
                placeholder="เลขที่ผู้เสียภาษี"
                InputProps={{ readOnly: true }}
                value={company?.company_id ? company?.company_id : ''}
                disabled={!company?.company_id}
              />
            </Grid>
            {company ? (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="ชื่อบริษัท"
                    variant="outlined"
                    placeholder="ชื่อบริษัท"
                    InputProps={{ readOnly: true }}
                    value={company?.company_name ? company.company_name : ''}
                    disabled={!company?.company_name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="รหัสบริษัท"
                    variant="outlined"
                    placeholder="รหัสบริษัท"
                    InputProps={{ readOnly: true }}
                    value={company?.company_code ? company?.company_code : ''}
                    disabled={!company?.company_code}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="อีเมล"
                    variant="outlined"
                    placeholder="อีเมล"
                    InputProps={{ readOnly: true }}
                    value={company?.email ? company?.email : ''}
                    disabled={!company?.email}
                  />
                </Grid>
              </>
            ) : null}
          </Grid>

          <Grid container spacing={2} mt={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="contractor-type">ประเภท Blacklist</InputLabel>
                  <Select
                    labelId="request-type"
                    label="ประเภทการร้องขอ"
                    name="requestType"
                    variant="outlined"
                    onChange={(e) =>
                      setBlacklistType(e.target.value == 'temporary' ? 'temporary' : 'permanent')
                    }
                    value={blacklist_companyType}
                    autoComplete="off"
                  >
                    <MenuItem value="permanent">ถาวร</MenuItem>
                    <MenuItem value="temporary">ชั่วคราว</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <DesktopDatePicker
                  label="วันที่สิ้นสุด Blacklist"
                  inputFormat="MM/dd/yyyy"
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled={blacklist_companyType == 'permanent'}
                      required={blacklist_companyType == 'temporary'}
                    />
                  )}
                  disabled={blacklist_companyType == 'permanent'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="สาเหตุ"
                  variant="outlined"
                  placeholder="สาเหตุ"
                  value={reason}
                  minRows={2}
                  multiline
                  onChange={(event) => setReason(event.target.value)}
                />
              </Grid>
            </LocalizationProvider>
          </Grid>
          <Grid container columns={2} spacing={2} direction={'row-reverse'}>
            <Grid item>
              <Button type="submit" variant="contained" color="primary" sx={{ marginTop: 4 }}>
                แก้ไข
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="error"
                sx={{ marginTop: 4 }}
                onClick={handleDelete}
              >
                ลบ
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Paper>
    </Container>
  );
};

export default BlacklistCompanyEdit;

const Container = styled('div')`
  padding: 16px;
`;

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
